import {
  UniversalCallPurpose,
  UniversalCallPacket,
  RetellOfficeAgentPayload,
  RetellLlmDynamicVariables,
  PINNED_RETELL_OFFICE_AGENT_ID,
  RETELL_OFFICE_AGENT_CONFIG,
  UNIVERSAL_CALL_PURPOSES
} from '../types/universalCall';
import { Client } from '../types';

/**
 * Strict data resolution:
 * Never fabricate defaults. If data is absent or empty, return "Unavailable".
 */
export const safeUnavailable = (value?: string | number | null): string => {
  if (value === undefined || value === null) return 'Unavailable';
  const str = String(value).trim();
  return str.length > 0 ? str : 'Unavailable';
};

/**
 * Builds the initial Call Packet prefilled from authoritative chart facts.
 * 
 * STRICT AUDIT RULES:
 * 1. Only map authoritative chart fields to their direct counterpart.
 * 2. Never synthesize target phone from patient/client phone (client.phone is a patient line).
 * 3. Never invent status strings (such as "Active Referral on File"). If absent, resolve to "Unavailable".
 * 4. Never invent document titles or synthesize dates from nearby unrelated fields.
 */
export function buildInitialCallPacket(
  client: Client | any,
  purpose: UniversalCallPurpose,
  clientDocs: any[] = [],
  clientFaxes: any[] = []
): UniversalCallPacket {
  // Authoritative Practice Name: client.physician_practice or client.physician_name.
  // STRICT: No fabricated fallback string.
  const practiceName = safeUnavailable(
    client?.physician_practice || client?.physician_name
  );

  // Authoritative Practice Target Phone: client.physician_phone.
  // STRICT: Do NOT synthesize from client.phone (which is the client/family personal line).
  const targetPhone = safeUnavailable(client?.physician_phone);

  // Department: Purpose default routing category
  const department = UNIVERSAL_CALL_PURPOSES[purpose]?.targetDepartmentDefault || 'Medical Records / Front Desk';

  // Authoritative Member Identity
  const memberFirstName = safeUnavailable(client?.first_name);
  const memberLastName = safeUnavailable(client?.last_name);
  const memberDob = safeUnavailable(client?.dob);
  const memberAhcccsId = safeUnavailable(client?.ahcccs_id);

  // Authoritative Address: formatted address or service address or home address
  const addressParts = [client?.address_line1, client?.city, client?.state, client?.zip].filter(Boolean);
  const clientAddress = addressParts.length > 0 ? addressParts.join(', ') : (client?.service_address || client?.home_address || null);

  const packet: UniversalCallPacket = {
    practice_name: practiceName,
    target_phone: targetPhone,
    department,
    member_first_name: memberFirstName,
    member_last_name: memberLastName,
    member_dob: memberDob,
    member_ahcccs_id: memberAhcccsId,
    address: safeUnavailable(clientAddress),
    member_phone: safeUnavailable(client?.phone),
    guardian_name: safeUnavailable(client?.guardian_name),
    guardian_phone: safeUnavailable(client?.guardian_phone),
    physician_name: safeUnavailable(client?.physician_name),
    target_name: practiceName !== 'Unavailable' ? practiceName : safeUnavailable(client?.physician_name),
    target_department: department,
    office_fax_on_file: safeUnavailable(client?.physician_fax),
    staff_instructions: '',
    routing_instructions: '',
    edited_fields: {}
  };

  if (purpose === 'VERIFY_MEMBER_ACTIVE_FAX') {
    // Authoritative lookup in communications faxes table
    const recentFax = clientFaxes && clientFaxes.length > 0
      ? clientFaxes.find((f: any) => f.client_id === client?.id || f.recipient_or_sender_name?.toLowerCase().includes(client?.last_name?.toLowerCase() || ''))
      : null;

    // Authoritative physician fax number from chart
    packet.active_fax_number = safeUnavailable(client?.physician_fax);

    // Authoritative recent fax date from faxes log (if any exists)
    packet.recent_fax_date = safeUnavailable(recentFax?.created_at?.slice(0, 10));

    // Authoritative recent fax document name (STRICT: no fabricated fallback)
    packet.recent_fax_document_type = safeUnavailable(recentFax?.document_name);

    // Authoritative chart referral/order info (STRICT: no fabricated "Active Referral on File")
    packet.active_order_status = safeUnavailable(client?.referral_order_info);

    // Neutral purpose template instruction: defines what to ask/verify rather than asserting factual status
    packet.staff_instructions = 'Inquire if the office has an active electronic fax line and verify whether the member is established.';
  } else if (purpose === 'CHECK_POC') {
    // 1. Authoritative lookup in clinical_documents table for selected POC
    const pocDoc = clientDocs && clientDocs.length > 0
      ? clientDocs.find((d: any) => d.doc_type === 'POC' || d.doc_type === 'PHYSICIAN_SIGNED_POC' || d.title?.toLowerCase().includes('plan of care'))
      : null;

    // Authoritative POC document title & identifier from documents table (STRICT: no fabricated fallback)
    packet.poc_document_title = safeUnavailable(pocDoc?.title);
    packet.poc_document_id = safeUnavailable(pocDoc?.id);

    // 2. Authoritative lookup for actual OUTGOING fax record associated with the selected POC
    // CRITICAL HARDENING:
    // Fax transmission facts (sent timestamp, destination, delivery status) may be treated
    // as authoritative ONLY when the outgoing fax record is explicitly linked to the selected POC/document
    // through a durable identifier (document_id or canonical doc-fax link).
    // Title/name similarity alone MUST NOT be treated as authoritative transmission evidence.
    let authoritativePocFax: any = null;
    let candidatePocFax: any = null;

    if (pocDoc && clientFaxes && clientFaxes.length > 0) {
      // Authoritative Match: Durable identifier linkage
      authoritativePocFax = clientFaxes.find((f: any) =>
        f.direction === 'OUTGOING' && (
          (f.document_id && pocDoc.id && String(f.document_id) === String(pocDoc.id)) ||
          (pocDoc.fax_id && f.id && String(f.id) === String(pocDoc.fax_id))
        )
      );

      // Unverified Candidate Match: Title/client similarity only (NOT authoritative)
      if (!authoritativePocFax && pocDoc.title) {
        candidatePocFax = clientFaxes.find((f: any) =>
          f.direction === 'OUTGOING' &&
          (f.client_id === client?.id || f.client_id === pocDoc.client_id) &&
          f.document_name && (
            f.document_name.toLowerCase() === pocDoc.title.toLowerCase() ||
            f.document_name.toLowerCase().includes(pocDoc.title.toLowerCase()) ||
            pocDoc.title.toLowerCase().includes(f.document_name.toLowerCase())
          )
        );
      }
    } else if (!pocDoc && clientFaxes && clientFaxes.length > 0) {
      // Check if unverified candidate faxes exist referencing POC for this client
      candidatePocFax = clientFaxes.find((f: any) =>
        f.direction === 'OUTGOING' &&
        f.client_id === client?.id &&
        (f.document_name?.toLowerCase().includes('plan of care') || f.document_name?.toLowerCase().includes('poc'))
      );
      if (candidatePocFax && (!packet.poc_document_title || packet.poc_document_title === 'Unavailable')) {
        packet.poc_document_title = safeUnavailable(candidatePocFax.document_name);
        packet.poc_document_id = safeUnavailable(candidatePocFax.document_id);
      }
    }

    // 3. Populate transmission evidence based on strict linkage verification
    if (authoritativePocFax) {
      // Durable canonical linkage confirmed: Authoritative evidence
      packet.poc_fax_evidence_link = 'EXPLICIT_ID_LINK';
      packet.poc_date_sent = safeUnavailable(authoritativePocFax.created_at?.slice(0, 10));
      packet.poc_fax_sent_at = safeUnavailable(authoritativePocFax.created_at);
      packet.poc_fax_destination = safeUnavailable(authoritativePocFax.recipient_or_sender_fax);

      let statusEvidence = authoritativePocFax.status || 'Unavailable';
      if (authoritativePocFax.provider_reference_id) {
        statusEvidence += ` (Ref: ${authoritativePocFax.provider_reference_id})`;
      }
      packet.poc_fax_delivery_status = safeUnavailable(statusEvidence);
    } else if (candidatePocFax) {
      // Title/name similarity only: Unverified candidate for staff review
      // Transmission facts remain strictly Unavailable to Retell and default packet
      packet.poc_fax_evidence_link = 'UNVERIFIED_CANDIDATE';
      packet.poc_fax_candidate_note = `Candidate outgoing fax found (${candidatePocFax.document_name || 'Outgoing Fax'} on ${candidatePocFax.created_at?.slice(0, 10) || 'unknown date'}) matching title only without a durable document ID link. Transmission facts are unverified and held as Unavailable.`;
      packet.poc_date_sent = 'Unavailable';
      packet.poc_fax_sent_at = 'Unavailable';
      packet.poc_fax_destination = 'Unavailable';
      packet.poc_fax_delivery_status = 'Unavailable';
    } else {
      // No link or candidate found
      packet.poc_fax_evidence_link = 'NONE';
      packet.poc_date_sent = 'Unavailable';
      packet.poc_fax_sent_at = 'Unavailable';
      packet.poc_fax_destination = 'Unavailable';
      packet.poc_fax_delivery_status = 'Unavailable';
    }

    // 4. Allegiant return fax:
    // Must come from authoritative configured source. If not available -> "Unavailable".
    const envReturnFax = typeof process !== 'undefined' && process.env?.ALLEGIANT_RETURN_FAX ? process.env.ALLEGIANT_RETURN_FAX : null;
    const windowReturnFax = typeof window !== 'undefined' && (window as any).__ALLEGIANT_RETURN_FAX__ ? (window as any).__ALLEGIANT_RETURN_FAX__ : null;
    packet.allegiant_return_fax = safeUnavailable(envReturnFax || windowReturnFax);

    // Authoritative POC certification end date from chart
    packet.poc_end_date = safeUnavailable(client?.poc_end_date);

    // Authoritative physician name from chart
    packet.physician_name = safeUnavailable(client?.physician_name);

    // Neutral purpose template inquiry objective
    packet.request_type = 'Verify Physician Signature / Certification Review Status';

    // Neutral purpose template instruction: defines what to ask
    packet.staff_instructions = 'Follow up on the pending Speech Therapy Plan of Care awaiting physician review or signature.';
  }

  return packet;
}

/**
 * Constructs the exact flat dynamic variables payload matching the Allegiant Office — Universal Call agent contract.
 * 
 * STRICT CONTRACT SPECIFICATIONS:
 * 1. Required Variables:
 *    - call_purpose
 *    - target_name
 *    - member_first_name
 *    - member_last_name
 *    - dob
 * 
 * 2. Optional Variables:
 *    - target_department
 *    - address
 *    - member_phone
 *    - guardian_name
 *    - guardian_phone
 *    - physician_name
 *    - office_fax_on_file
 *    - poc_identifier
 *    - poc_title
 *    - fax_sent_at
 *    - fax_sent_to
 *    - delivery_evidence_summary
 *    - allegiant_return_fax
 *    - staff_instructions
 *    - routing_instructions
 * 
 * 3. All values passed in retell_llm_dynamic_variables MUST be strings.
 * 4. Nested structures (member_identity, physician_practice, optional_routing_context, verification_context) are completely eliminated.
 * 5. Non-contract variables (agent_id, agent_version, to_number, purpose, purpose_label) are excluded from dynamic variables.
 * 6. Use call_purpose, not purpose.
 * 7. Missing optional values remain empty/unavailable strings as defined by the reviewed packet. No fabricated facts.
 */
export function buildRetellDynamicVariables(
  packet: UniversalCallPacket,
  purpose: UniversalCallPurpose
): RetellLlmDynamicVariables {
  // Required
  const callPurpose = String(purpose || packet.call_purpose || 'VERIFY_MEMBER_ACTIVE_FAX');
  const targetName = String(packet.target_name || packet.practice_name || packet.physician_name || 'Unavailable');
  const memberFirstName = String(packet.member_first_name || 'Unavailable');
  const memberLastName = String(packet.member_last_name || 'Unavailable');
  const dob = String(packet.dob || packet.member_dob || 'Unavailable');

  // Optional
  const targetDepartment = String(packet.target_department || packet.department || 'Medical Records / Front Desk');
  const address = String(packet.address || packet.member_address || 'Unavailable');
  const memberPhone = String(packet.member_phone || 'Unavailable');
  const guardianName = String(packet.guardian_name || 'Unavailable');
  const guardianPhone = String(packet.guardian_phone || 'Unavailable');
  const physicianName = String(packet.physician_name || 'Unavailable');

  let officeFaxOnFile = 'Unavailable';
  if (packet.office_fax_on_file && packet.office_fax_on_file !== 'Unavailable') {
    officeFaxOnFile = String(packet.office_fax_on_file);
  } else if (purpose === 'VERIFY_MEMBER_ACTIVE_FAX' && packet.active_fax_number && packet.active_fax_number !== 'Unavailable') {
    officeFaxOnFile = String(packet.active_fax_number);
  } else if (purpose === 'CHECK_POC' && packet.poc_fax_destination && packet.poc_fax_destination !== 'Unavailable') {
    officeFaxOnFile = String(packet.poc_fax_destination);
  }

  let pocIdentifier = 'Unavailable';
  let pocTitle = 'Unavailable';
  let faxSentAt = 'Unavailable';
  let faxSentTo = 'Unavailable';
  let deliveryEvidenceSummary = 'Unavailable';

  if (purpose === 'CHECK_POC') {
    pocIdentifier = String(packet.poc_identifier || packet.poc_document_id || 'Unavailable');
    pocTitle = String(packet.poc_title || packet.poc_document_title || 'Unavailable');

    const isExplicitLink = packet.poc_fax_evidence_link === 'EXPLICIT_ID_LINK';
    const hasStaffTimestamp = Boolean(packet.edited_fields?.poc_fax_sent_at || packet.edited_fields?.fax_sent_at);
    const hasStaffDestination = Boolean(packet.edited_fields?.poc_fax_destination || packet.edited_fields?.fax_sent_to);
    const hasStaffStatus = Boolean(packet.edited_fields?.poc_fax_delivery_status || packet.edited_fields?.delivery_evidence_summary);

    if (isExplicitLink || hasStaffTimestamp) {
      faxSentAt = String(packet.fax_sent_at || packet.poc_fax_sent_at || packet.poc_date_sent || 'Unavailable');
    }
    if (isExplicitLink || hasStaffDestination) {
      faxSentTo = String(packet.fax_sent_to || packet.poc_fax_destination || 'Unavailable');
    }
    if (isExplicitLink || hasStaffStatus) {
      deliveryEvidenceSummary = String(packet.delivery_evidence_summary || packet.poc_fax_delivery_status || 'Unavailable');
    }
  } else if (purpose === 'VERIFY_MEMBER_ACTIVE_FAX') {
    if (packet.recent_fax_date && packet.recent_fax_date !== 'Unavailable') {
      faxSentAt = String(packet.recent_fax_date);
    }
  }

  const allegiantReturnFax = String(packet.allegiant_return_fax || 'Unavailable');
  const staffInstructions = String(packet.staff_instructions ? packet.staff_instructions.trim() : 'Unavailable');
  const routingInstructions = String(packet.routing_instructions ? packet.routing_instructions.trim() : 'Unavailable');

  return {
    call_purpose: callPurpose,
    target_name: targetName,
    member_first_name: memberFirstName,
    member_last_name: memberLastName,
    dob: dob,
    target_department: targetDepartment,
    address: address,
    member_phone: memberPhone,
    guardian_name: guardianName,
    guardian_phone: guardianPhone,
    physician_name: physicianName,
    office_fax_on_file: officeFaxOnFile,
    poc_identifier: pocIdentifier,
    poc_title: pocTitle,
    fax_sent_at: faxSentAt,
    fax_sent_to: faxSentTo,
    delivery_evidence_summary: deliveryEvidenceSummary,
    allegiant_return_fax: allegiantReturnFax,
    staff_instructions: staffInstructions,
    routing_instructions: routingInstructions
  };
}

/**
 * Constructs the UI preview payload for the Retell agent call.
 */
export function buildRetellPreviewPayload(
  packet: UniversalCallPacket,
  purpose: UniversalCallPurpose
): RetellOfficeAgentPayload {
  const dynamicVars = buildRetellDynamicVariables(packet, purpose);

  return {
    agent_id: RETELL_OFFICE_AGENT_CONFIG.agent_id || PINNED_RETELL_OFFICE_AGENT_ID,
    agent_version: RETELL_OFFICE_AGENT_CONFIG.agent_version || '0',
    to_number: packet.target_phone,
    purpose,
    purpose_label: UNIVERSAL_CALL_PURPOSES[purpose]?.label || purpose,
    ...dynamicVars
  };
}

/**
 * Formats a preview specimen of the canonical Client Note format for UI display.
 * Purely presentation-level — does NOT create or mutate notes, and does not require an authoritative intent.
 */
/** The number staff reviewed. Chart physician_phone is not a separate dial field. */
export function reviewedDestinationNumber(packet: { target_phone?: string | null } | null | undefined): string {
  const raw = String(packet?.target_phone ?? '');
  const trimmed = raw.trim();
  if (!trimmed || trimmed === 'Unavailable' || trimmed === 'undefined' || trimmed === 'null') return '';
  return raw;
}

export function isReviewableDestination(value: unknown): boolean {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length >= 10;
}

export function formatSpecimenPreviewNote(
  preview: RetellOfficeAgentPayload | RetellLlmDynamicVariables | any,
  purpose: UniversalCallPurpose
): string {
  const purposeLabel = preview.purpose_label || UNIVERSAL_CALL_PURPOSES[purpose]?.label || purpose;
  const practice = preview.target_name || preview.physician_practice?.name || 'practice';
  const phone = preview.to_number || preview.office_fax_on_file || 'phone';

  if (purpose === 'VERIFY_MEMBER_ACTIVE_FAX') {
    return `Universal Call — ${purposeLabel}. Called ${practice} at ${phone}. Spoke with Medical Records Coordinator. Office reported: Active electronic fax line confirmed. Referral accepted for scheduling. Duration: 1m 18s. Office requested resend facesheet with updated subscriber ID.`;
  } else {
    return `Universal Call — ${purposeLabel}. Called ${practice} at ${phone}. Spoke with Medical Records Coordinator. Office reported: Physician reviewed and signed Plan of Care. Returning via fax today. Duration: 1m 05s.`;
  }
}
